import React, { useEffect, useMemo, useState } from 'react';
import { Mail, Shield, UserCog } from 'lucide-react';
import { toast } from 'sonner';

import DataTable from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import LoadingSkeleton from '../components/LoadingSkeleton';
import StatusBadge from '../components/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { usersApi } from '../lib/api';

const ROLE_META = {
  owner: 'bg-purple-500/20 text-purple-300',
  security_admin: 'bg-red-500/20 text-red-300',
  it_admin: 'bg-blue-500/20 text-blue-300',
  manager: 'bg-emerald-500/20 text-emerald-300',
  auditor: 'bg-slate-500/20 text-slate-300',
};

function formatTimestamp(value) {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [form, setForm] = useState({ email: '', role: 'manager' });
  const pageSize = 12;

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await usersApi.list({ page, page_size: pageSize });
      setUsers(response.data?.items || []);
      setTotal(response.data?.total || 0);
    } catch (err) {
      const message = err.response?.data?.detail || 'Failed to load users.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [page]);

  const inviteUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await usersApi.create(form);
      toast.success(response.data?.message || 'Invite sent.');
      setShowInvite(false);
      setForm({ email: '', role: 'manager' });
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite user.');
    } finally {
      setSaving(false);
    }
  };

  const updateRole = async (user, role) => {
    try {
      await usersApi.update(user.id, { role });
      toast.success('Role updated.');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role.');
    }
  };

  const toggleActive = async (user) => {
    try {
      await usersApi.update(user.id, { is_active: !user.is_active });
      toast.success(user.is_active ? 'User deactivated.' : 'User activated.');
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user.');
    }
  };

  const userRows = useMemo(() => users, [users]);

  return (
    <div className="min-h-screen space-y-6 bg-slate-900 p-6 text-slate-300" data-testid="users-page">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="mt-2 text-slate-500">
            Invite administrators, manage role assignments, and activate or deactivate access.
          </p>
        </div>
        <Button className="bg-blue-600 text-white hover:bg-blue-700" onClick={() => setShowInvite(true)}>
          <Mail className="h-4 w-4" />
          Invite User
        </Button>
      </div>

      <Card className="border-slate-700 bg-slate-800 shadow-lg">
        <CardHeader className="pb-0">
          <CardTitle className="text-white">Administrative Access</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          {error ? (
            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4 text-sm text-red-300">
              {error}
            </div>
          ) : null}

          {loading ? (
            <LoadingSkeleton type="table" />
          ) : userRows.length ? (
            <DataTable
              columns={[
                { key: 'email', label: 'Email' },
                {
                  key: 'role',
                  label: 'Role',
                  render: (value) => (
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${ROLE_META[value] || ROLE_META.auditor}`}
                    >
                      {String(value).replace(/_/g, ' ')}
                    </span>
                  ),
                },
                {
                  key: 'status',
                  label: 'Status',
                  render: (_, row) => <StatusBadge status={row.is_active ? 'online' : 'offline'} />,
                },
                {
                  key: 'last_login',
                  label: 'Last Login',
                  render: (value) => formatTimestamp(value),
                },
                {
                  key: 'actions',
                  label: 'Actions',
                  render: (_, row) => (
                    <div className="flex gap-2">
                      <select
                        value={row.role}
                        onChange={(event) => updateRole(row, event.target.value)}
                        className="rounded-md border border-slate-600 bg-slate-900 px-2 py-1 text-xs text-slate-200 outline-none"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <option value="auditor">Auditor</option>
                        <option value="manager">Manager</option>
                        <option value="it_admin">IT Admin</option>
                        <option value="security_admin">Security Admin</option>
                        <option value="owner">Owner</option>
                      </select>
                      <Button
                        size="sm"
                        className={row.is_active ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-blue-600 text-white hover:bg-blue-700'}
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleActive(row);
                        }}
                      >
                        {row.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  ),
                },
              ]}
              data={userRows}
              loading={loading}
              emptyMessage="No admin users are configured."
              pagination={{
                page,
                pageSize,
                total,
                onPageChange: setPage,
              }}
            />
          ) : (
            <EmptyState
              icon={<UserCog className="h-8 w-8" />}
              title="No users found"
              description="Invite an administrative user to grant access to this workspace."
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="border-slate-700 bg-slate-800 text-slate-300">
          <DialogHeader>
            <DialogTitle className="text-white">Invite User</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={inviteUser}>
            <input
              type="email"
              value={form.email}
              onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
              placeholder="user@company.com"
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <select
              value={form.role}
              onChange={(event) => setForm((current) => ({ ...current, role: event.target.value }))}
              className="w-full rounded-lg border border-slate-600 bg-slate-700 px-4 py-3 text-white outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="auditor">Auditor</option>
              <option value="manager">Manager</option>
              <option value="it_admin">IT Admin</option>
              <option value="security_admin">Security Admin</option>
            </select>
            <Button className="w-full bg-blue-600 text-white hover:bg-blue-700" disabled={saving} type="submit">
              {saving ? 'Sending...' : 'Send Invite'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
